import { motion } from 'framer-motion'
import type { TeamStanding } from '@gymboard/types'
import { formatTeamScores } from './teamScore'

/**
 * Team battle scoreboard — sits above the individual leaderboard while the
 * coach has split the class into teams. Two pieces, readable from across
 * the room:
 *   1. A tug-of-war bar: each team's share of the combined score, sliding
 *      as the battle swings.
 *   2. Team cards in fixed team order (not rank order, so cards don't
 *      jump around): name, average effort, member count, crown on the
 *      leader.
 * Scores are member averages (see TeamStanding), so the bar measures who
 * is working harder, not who has more people.
 */
export default function TeamBattleBar({ teams }: { teams: TeamStanding[] }) {
  if (teams.length === 0) return null
  const byTeam = [...teams].sort((a, b) => a.team - b.team)
  const total = byTeam.reduce((sum, t) => sum + Math.max(0, t.score), 0)
  const leaderScore = Math.max(...byTeam.map((t) => t.score))
  const fmt = formatTeamScores(byTeam)
  const hasLeader = leaderScore > 0 && byTeam.filter((t) => t.score === leaderScore).length === 1

  return (
    <div className="relative z-10 flex-shrink-0 px-8 pt-3 pb-1">
      {/* Tug of war */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-white/[0.06]">
        {byTeam.map((t) => (
          <motion.div
            key={t.team}
            className="h-full"
            style={{ backgroundColor: t.color }}
            initial={false}
            animate={{ width: total > 0 ? `${(Math.max(0, t.score) / total) * 100}%` : `${100 / byTeam.length}%` }}
            transition={{ type: 'spring', stiffness: 120, damping: 22 }}
          />
        ))}
      </div>

      {/* Team cards */}
      <div className="mt-3 grid gap-3" style={{ gridTemplateColumns: `repeat(${byTeam.length}, minmax(0, 1fr))` }}>
        {byTeam.map((t) => {
          const leading = hasLeader && t.score === leaderScore
          return (
            <motion.div
              key={t.team}
              layout
              className="relative flex items-center justify-between gap-3 rounded-2xl px-5 py-3 overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${t.color}2e, ${t.color}0d)`,
                border: `1px solid ${t.color}${leading ? 'cc' : '4d'}`,
                boxShadow: leading ? `0 0 28px ${t.color}55` : undefined,
              }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                <div className="min-w-0">
                  <p className="text-white font-black text-xl leading-tight truncate">
                    Team {t.name}
                    {leading && (
                      <span className="ml-2 align-middle rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: t.color, color: '#0E1022' }}>
                        Ahead
                      </span>
                    )}
                  </p>
                  <p className="text-white/40 text-xs">
                    {t.members} {t.members === 1 ? 'athlete' : 'athletes'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <motion.p
                  key={fmt(t.score)}
                  initial={{ scale: 1.12, opacity: 0.7 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                  className="font-black tabular-nums leading-none"
                  style={{ color: t.color, fontSize: 'clamp(1.8rem, 3vw, 2.6rem)' }}
                >
                  {fmt(t.score)}
                </motion.p>
                <p className="text-white/30 text-[10px] uppercase tracking-widest mt-1">avg effort</p>
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
